/**
 * Jeu d'icônes « Méridien » — tracés repris à l'identique du prototype de design.
 *
 * Volontairement séparé de `iconSets.ts` (lucide/phosphor) : ces tracés sont la
 * signature visuelle du thème Méridien (trait 1.8, grille 24, bouts arrondis) et
 * ne doivent pas dériver vers une librairie tierce. Les autres thèmes continuent
 * d'utiliser leur set d'icônes habituel.
 */
interface IconProps {
  className?: string;
  size?: number;
}

function Svg({ children, className, size = 21, strokeWidth = 1.8 }: IconProps & { children: React.ReactNode; strokeWidth?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      className={className}
      aria-hidden
    >
      {children}
    </svg>
  );
}

export function IconDashboard(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
    </Svg>
  );
}

/** Compte commun — deux silhouettes (le duo) */
export function IconShared(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="9" cy="8" r="3.2" />
      <circle cx="16" cy="9" r="2.6" />
      <path d="M3.5 19c0-3 2.5-4.8 5.5-4.8s5.5 1.8 5.5 4.8" />
      <path d="M15.5 14.4c2.6.2 4.5 1.9 4.5 4.6" />
    </Svg>
  );
}

/** Mon budget — carte bancaire */
export function IconPersonal(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="6" width="18" height="13" rx="3" />
      <path d="M3 10h18" />
      <circle cx="16.5" cy="14.5" r="1.3" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Enveloppes budgétaires */
export function IconEnvelope(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </Svg>
  );
}

/** Analyses / recherche d'opérations */
export function IconStats(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" strokeLinecap="round" />
    </Svg>
  );
}

export function IconSettings(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v2.6M12 18.9v2.6M21.5 12h-2.6M5.1 12H2.5M18.7 5.3l-1.8 1.8M7.1 16.9l-1.8 1.8M18.7 18.7l-1.8-1.8M7.1 7.1L5.3 5.3" />
    </Svg>
  );
}

export function IconSearch(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" strokeLinecap="round" />
    </Svg>
  );
}

export function IconShield(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 3l7 3v5.5c0 4.3-2.9 8-7 9.5-4.1-1.5-7-5.2-7-9.5V6l7-3z" strokeLinejoin="round" />
    </Svg>
  );
}

export function IconChevron(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={2}>
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function IconPin({ filled, ...p }: IconProps & { filled?: boolean }) {
  return (
    <Svg {...p}>
      <path d="M12 17v5" strokeLinecap="round" />
      <path d="M9 3h6l-1 6 3 3H7l3-3-1-6z" fill={filled ? 'currentColor' : 'none'} strokeLinejoin="round" />
    </Svg>
  );
}

export function IconSun(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={1.9}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2v2.4M12 19.6V22M22 12h-2.4M4.4 12H2M19.1 4.9l-1.7 1.7M6.6 17.4l-1.7 1.7M19.1 19.1l-1.7-1.7M6.6 6.6L4.9 4.9" strokeLinecap="round" />
    </Svg>
  );
}

export function IconMoon(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={1.9}>
      <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2z" strokeLinejoin="round" />
    </Svg>
  );
}

export function IconMonitor(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={1.9}>
      <rect x="2.5" y="4" width="19" height="13" rx="2.5" />
      <path d="M8.5 21h7M12 17v4" strokeLinecap="round" />
    </Svg>
  );
}

export function IconLogout(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M14 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8" strokeLinecap="round" />
      <path d="M17 15l3-3-3-3M20 12H10" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function IconMenu(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </Svg>
  );
}
